import {
  clamp,
  entre,
  GRADOS_A_RAD,
  Sacada,
  signo,
  smoothstep,
  VIDA_DEFECTOS,
  type Rng,
  type VidaOpts,
} from './vida';

/**
 * Capas del avatar que siguen al turno (tanda 8, pieza 5): "pensando"
 * mientras se procesa la pregunta del alumno, e "inspiración" en el
 * pre-roll antes de que suene la respuesta.
 *
 * Las dispara el orquestador por la fase del turno, que es estado que el
 * cliente conoce. **No son emociones**: no miran qué expresión está
 * aplicada y se componen sobre ella —incluido el decay de la pieza 4—.
 * Como las otras capas, solo producen desplazamientos; el que los compone
 * es el `ThreeRigAdapter`.
 */

/** Lo que aporta "pensando" en un instante. */
export interface PensandoOffsets {
  /** Desvío de mirada, se suma a `mirada_x`. */
  miradaX: number;
  /** Desvío de mirada, se suma a `mirada_y`. */
  miradaY: number;
  /** Peso que se suma a `mouthPressLeft` y `mouthPressRight`. */
  presion: number;
}

export const PENSANDO_REPOSO: Readonly<PensandoOffsets> = { miradaX: 0, miradaY: 0, presion: 0 };

type FasePensando = 'inactiva' | 'esperando' | 'fuera' | 'regreso';

/**
 * Pensando: a los ~400 ms de entrar en `procesando` mira fuera de cámara
 * —arriba y a un lado— con regresos breves a cámara cada 1,5–3 s, y aprieta
 * apenas los labios. Al desactivarse vuelve a cámara con una sacada.
 *
 * El parpadeo más lento no vive acá: el adapter baja la tasa de la idle
 * mientras esta capa está activa.
 */
export class CapaPensando {
  private vida: Readonly<VidaOpts>;
  private readonly rng: Rng;
  private readonly mirada = new Sacada();
  private fase: FasePensando = 'inactiva';
  private restanteMs = 0;
  private presion = 0;
  private presionObjetivo = 0;
  private readonly salida: PensandoOffsets = { miradaX: 0, miradaY: 0, presion: 0 };

  constructor(opts: { rng?: Rng; vida?: Readonly<VidaOpts> } = {}) {
    this.rng = opts.rng ?? Math.random;
    this.vida = opts.vida ?? VIDA_DEFECTOS;
  }

  configurar(vida: Readonly<VidaOpts>): void {
    this.vida = vida;
  }

  /** Si el turno está en proceso, haya empezado a moverse o no. */
  get pensando(): boolean {
    return this.fase !== 'inactiva';
  }

  /** Si todavía aporta algo: pensando, o volviendo de pensar. */
  get activo(): boolean {
    return this.pensando || this.presion > 0 || this.mirada.x !== 0 || this.mirada.y !== 0;
  }

  activar(): void {
    if (this.pensando) return;
    this.fase = 'esperando';
    this.restanteMs = this.vida.pensandoRetrasoMs;
  }

  desactivar(): void {
    if (!this.pensando) return;
    this.fase = 'inactiva';
    this.presionObjetivo = 0;
    this.irA(0, 0);
  }

  update(deltaMs: number): PensandoOffsets {
    const v = this.vida;
    if (this.fase !== 'inactiva') {
      this.restanteMs -= deltaMs;
      if (this.restanteMs <= 0) {
        if (this.fase === 'fuera') {
          this.fase = 'regreso';
          this.restanteMs = entre(this.rng, v.pensandoRegresoMinMs, v.pensandoRegresoMaxMs);
          this.irA(0, 0);
        } else {
          // Desde la espera o desde un regreso: vuelve a mirar afuera.
          if (this.fase === 'esperando') {
            this.presionObjetivo = entre(this.rng, v.pensandoPresionMin, v.pensandoPresionMax);
          }
          this.fase = 'fuera';
          this.restanteMs = entre(this.rng, v.pensandoFueraMinMs, v.pensandoFueraMaxMs);
          this.irA(
            signo(this.rng) * entre(this.rng, v.pensandoMiradaXMin, v.pensandoMiradaXMax),
            entre(this.rng, v.pensandoMiradaYMin, v.pensandoMiradaYMax),
          );
        }
      }
    }

    // Rampa lineal de los labios: entra y sale en `pensandoRampaMs`.
    const paso = (Math.max(v.pensandoPresionMax, 1e-3) * deltaMs) / Math.max(1, v.pensandoRampaMs);
    this.presion =
      this.presion < this.presionObjetivo
        ? Math.min(this.presionObjetivo, this.presion + paso)
        : Math.max(this.presionObjetivo, this.presion - paso);

    this.mirada.update(deltaMs);
    const s = this.salida;
    s.miradaX = this.mirada.x;
    s.miradaY = this.mirada.y;
    s.presion = this.presion;
    return s;
  }

  reiniciar(): void {
    this.fase = 'inactiva';
    this.presion = this.presionObjetivo = 0;
    this.mirada.reiniciar();
    this.salida.miradaX = this.salida.miradaY = this.salida.presion = 0;
  }

  private irA(x: number, y: number): void {
    this.mirada.ir(x, y, entre(this.rng, this.vida.sacadaMinMs, this.vida.sacadaMaxMs));
  }
}

/** Lo que aporta la inspiración en un instante. */
export interface InspiracionOffsets {
  /** Peso que se suma a `jawOpen`. */
  mandibula: number;
  /** Cabeceo en radianes. Positivo = hacia arriba. */
  cabezaPitch: number;
}

export const INSPIRACION_REPOSO: Readonly<InspiracionOffsets> = { mandibula: 0, cabezaPitch: 0 };

/**
 * Inspiración del pre-roll: en los últimos `inspiracionMs` antes de que
 * arranque el audio, la mandíbula se abre apenas y la cabeza sube un
 * grado, como quien toma aire para hablar. Cuando la voz arranca
 * (`soltar`), vuelve a cero en `inspiracionSoltarMs` y el habla toma la
 * boca.
 */
export class CapaInspiracion {
  private vida: Readonly<VidaOpts>;
  private readonly rng: Rng;
  /** Tiempo desde el inicio del pre-roll; `null` = sin pre-roll. */
  private tMs: number | null = null;
  private totalMs = 0;
  private jaw = 0;
  /** Nivel `0..1` de la inspiración. */
  private nivel = 0;
  private soltando = false;
  private readonly salida: InspiracionOffsets = { mandibula: 0, cabezaPitch: 0 };

  constructor(opts: { rng?: Rng; vida?: Readonly<VidaOpts> } = {}) {
    this.rng = opts.rng ?? Math.random;
    this.vida = opts.vida ?? VIDA_DEFECTOS;
  }

  configurar(vida: Readonly<VidaOpts>): void {
    this.vida = vida;
  }

  get activo(): boolean {
    return this.tMs !== null || this.nivel > 0;
  }

  /** Empieza un pre-roll de `prerollMs`: la inspiración ocupa su final. */
  iniciar(prerollMs: number): void {
    this.tMs = 0;
    this.totalMs = Math.max(0, prerollMs);
    this.jaw = entre(this.rng, this.vida.inspiracionJawMin, this.vida.inspiracionJawMax);
    this.soltando = false;
  }

  /** Arrancó la voz: la inspiración se suelta. */
  soltar(): void {
    if (this.tMs === null && this.nivel === 0) return;
    this.tMs = null;
    this.soltando = true;
  }

  update(deltaMs: number): InspiracionOffsets {
    const v = this.vida;
    if (this.tMs !== null) {
      this.tMs += deltaMs;
      const inicio = this.totalMs - v.inspiracionMs;
      this.nivel = smoothstep((this.tMs - inicio) / Math.max(1, v.inspiracionMs));
      // Si la voz no llega (audio que no suena), no se queda con la boca
      // abierta: se suelta sola poco después del final previsto.
      if (this.tMs > this.totalMs + v.inspiracionSoltarMs * 2) this.soltar();
    } else if (this.soltando) {
      this.nivel = clamp(this.nivel - deltaMs / Math.max(1, v.inspiracionSoltarMs), 0, 1);
      if (this.nivel === 0) this.soltando = false;
    }
    const s = this.salida;
    s.mandibula = this.nivel * this.jaw;
    s.cabezaPitch = this.nivel * v.inspiracionPitchGrados * GRADOS_A_RAD;
    return s;
  }

  reiniciar(): void {
    this.tMs = null;
    this.nivel = 0;
    this.soltando = false;
    this.salida.mandibula = this.salida.cabezaPitch = 0;
  }
}
